import {FormattedMessage, useIntl} from "react-intl";
import React, {useEffect, useMemo, useRef, useState} from "react";
import '../scss/SurveyRecipients.scss';
import {useContactsData, useSupportRolesData, useSurveyData, useUserData} from "./SurveyView";
import { useTable, useRowSelect, useSortBy, useAsyncDebounce, useFilters, useGlobalFilter, useFlexLayout, usePagination, useColumnOrder } from 'react-table'
import {getAnnieuserForContactWithRole, getStudentFromContacts, getStudentName} from "../DataFunctions";
import {ReactComponent as CloseIcon} from "../svg/close.svg";
import {ReactComponent as Minimize} from "../svg/minimize-s.svg";
import {ReactComponent as Maximize} from "../svg/maximize.svg";
import {ReactComponent as Previous} from "../svg/previous.svg";
import {ReactComponent as Next} from "../svg/next.svg";
import {ReactComponent as First} from "../svg/first.svg";
import {ReactComponent as Last} from "../svg/last.svg";
import {ColumnEditor, ContactsTable, FiltersDisplay, UploadContacts} from "./Contacts";
import {ReactComponent as UpArrow} from "../svg/up-arrow.svg";
import {ReactComponent as DownArrow} from "../svg/down-arrow.svg";
import {
    GetAppTranslations,
    GetContacts,
    GetGeneralTranslations,
    GetUserAndRoleOfContact,
    GetUsers
} from "../api/APISurvey";
import {ReactComponent as UserIcon} from "../svg/provider.svg";
import {GetContactMeta, GetContactsAndRoles, GetUserMeta, PostTranslation} from "../api/APIContact";
import {ReactComponent as RefreshIcon} from "../svg/refresh.svg";
import {useDataContext} from "./SurveyContextProvider";

export default function SurveyRecipients() {
    const [dataFromUpload, setDataFromUpload] = useState([])
    const intl = useIntl()
    const { value: supportRolesData } = useDataContext('supportRolesData')
    const { value: userMeta } = useDataContext('userMeta')
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"

    const { value: userData, setValue: setUserData } = useDataContext('userData')
    const getUserData = GetUsers()
    const previousUserDataRef = useRef()

    useEffect(() => {
        if (getUserData.isSuccess) {
            const newData = getUserData.data

            // Check if the data has changed since the last update
            if (newData !== previousUserDataRef.current) {
                setUserData(newData)
                previousUserDataRef.current = newData // Store the new data for future comparisons
            }
        }
    }, [getUserData])

    const { value: contactsData, setValue: setContactsData } = useDataContext('contactsData')
    const getContacts = GetContacts()
    const previousContactsDataRef = useRef()

    useEffect(() => {
        if (getContacts.isSuccess) {
            const newData = getContacts.data

            // Check if the data has changed since the last update
            if (newData !== previousContactsDataRef.current) {
                setContactsData(newData)
                previousContactsDataRef.current = newData
            }
        }
    }, [getContacts])

    const { value: contactsAndRoles, setValue: setContactsAndRoles } = useDataContext('contactsAndRoles')
    const getContactsAndRoles = GetContactsAndRoles()
    const previousContactsAndRolesRef = useRef()

    useEffect(() => {
        if (getContactsAndRoles.isSuccess) {
            const newData = getContactsAndRoles.data

            // Check if the data has changed since the last update
            if (newData !== previousContactsAndRolesRef.current) {
                setContactsAndRoles(newData)
                previousContactsAndRolesRef.current = newData
            }
        }
    }, [getContactsAndRoles])

    const { value: contactMeta, setValue: setContactMeta } = useDataContext('contactMeta')
    const getContactMeta = GetContactMeta()
    const previousContactMetaRef = useRef()

    useEffect(() => {
        if (getContactMeta.isSuccess) {
            const newData = getContactMeta?.data[0]

            // Check if the data has changed since the last update
            if (newData !== previousContactMetaRef.current) {
                setContactMeta(newData)
                previousContactMetaRef.current = newData
            }
        }
    }, [getContactMeta])

    const getTranslations = GetAppTranslations(intl.locale)
    const getGeneralTranslations = GetGeneralTranslations(intl.locale)

    return <div className={"survey-recipients"}>
        <SurveyRecipientsContent contactsData={contactsAndRoles?.contacts}/>
        <ContactsTable
            contacts={contactsAndRoles?.contacts}
            contactMeta={contactMeta}
            supportRolesData={supportRolesData}
            refetchContacts={()=> {
                getContactsAndRoles.refetch()
                getContacts.refetch()
                getContactMeta.refetch()
                getTranslations.refetch()
                getGeneralTranslations.refetch()
            }}
            refetchingContacts={getContactsAndRoles.isRefetching}
            surveyRecipients={true}
            surveyData={surveyData}
            setSurveyData={setSurveyData}
            userData={userData}
            userMeta={userMeta}
            refetchUsers={()=>getUserData.refetch()}
        />
    </div>
}

function SurveyRecipientsContent({contactsData}) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const surveyContacts = surveyData.contacts
    const intl = useIntl()
    const [showContacts, setShowContacts] = useState(false)
    const [showEmptyConfirm, setShowEmptyConfirm] = useState(false)
    const [showSummary, setShowSummary] = useState(localStorage.getItem("showRecipientSummary") ? JSON.parse(localStorage.getItem("showRecipientSummary")) : true)
    const limitedEditing = surveyData.status === "IN PROGRESS" || surveyData.status === "FINISHED"

    const removeStudentFromSurvey = (user) => {
        const newSurveyData = {...surveyData}
        const index = newSurveyData.contacts.indexOf(user)
        if (index > -1) {
            newSurveyData.contacts.splice(index, 1)
        }
        setSurveyData(newSurveyData)
    }

    const emptySurveyOfContacts = () => {
        const newSurveyData = {...surveyData}
        newSurveyData.contacts = []
        setSurveyData(newSurveyData)
        setShowEmptyConfirm(false)
    }

    let studentList
    studentList = surveyContacts && !showContacts ? surveyContacts.slice(0,8) : surveyContacts

    const surveyContactsDetails = surveyContacts && surveyContacts.map(student => getStudentFromContacts(student, contactsData))
    const surveyDegrees = surveyContactsDetails && surveyContactsDetails.map(student => student?.meta && student["meta"].hasOwnProperty("degree") && student["meta"]["degree"])
    const surveyGroups = surveyContactsDetails && surveyContactsDetails.map(student => student?.meta && student["meta"].hasOwnProperty("group") && student["meta"]["group"])
    const surveyLocations = surveyContactsDetails && surveyContactsDetails.map(student => student?.meta && student["meta"].hasOwnProperty("location") && student["meta"]["location"])

    const summaryData = [
        {
            header: intl.formatMessage({id:'group', defaultMessage:'Groups'}),
            keys: surveyGroups && surveyGroups.filter(filterUnique),
            allKeys: surveyGroups
        },
        {
            header: intl.formatMessage({id:'degree', defaultMessage:'Degrees'}),
            keys: surveyDegrees && surveyDegrees.filter(filterUnique),
            allKeys: surveyDegrees
        },
        {
            header: intl.formatMessage({id:'location', defaultMessage:'Locations'}),
            keys: surveyLocations && surveyLocations.filter(filterUnique),
            allKeys: surveyLocations
        }
    ]

    function filterUnique(value, index, self) {
        return self.indexOf(value) === index
    }

    const RenderSummary = () => {
        return summaryData.map((obj, i) => {
            return <RenderSummaryGroup obj={obj} key={i} />
        })
    }

    function RenderSummaryGroup({obj}) {
        const [maximizeGroup, setMaximizeGroup] = useState(false)
        const keys = obj && maximizeGroup ? obj.keys : obj.keys.slice(0,5)
        const extra = obj.keys.length - 5

        return <div className={"summary-group"}>
            <p>
                {obj.header} ({obj.keys.length}):
            </p>
            <div className={"summary-objects-container"}>
                {keys.map((key, i) => {
                    let amount = 0
                    for (let i = 0; i < obj.allKeys.length; i++) {
                        if (obj.allKeys[i] === key) {
                            amount++
                        }
                    }
                    if (key) {
                        return <div className={"summary-object"} key={i}>{key} ({amount})</div>
                    }
                    return <div className={"summary-object"} key={i}>
                        {intl.formatMessage(
                            {
                                id: 'survey.table.empty',
                                defaultMessage: '(Empty)',
                            })} ({amount})</div>
                })}
                {extra > 0 && !maximizeGroup &&
                    <button className={"text"} onClick={()=>setMaximizeGroup(true)}>
                        + {extra} {intl.formatMessage(
                        {
                            id: 'survey.table.more',
                            defaultMessage: 'more',
                        })}</button>
                }
                {maximizeGroup &&
                <button className={"text"} onClick={()=>setMaximizeGroup(false)}>
                    - {intl.formatMessage(
                    {
                        id: 'minimize',
                        defaultMessage: 'Minimize',
                    })}
                </button>
                }
            </div>
        </div>
    }

    return <div className={"survey-recipients-container"}>
        <h4>
            <span>
            {intl.formatMessage(
                {
                    id: 'survey.contacts.recipients',
                    defaultMessage: 'Survey Recipients',
                })}
            {surveyContacts && !surveyContacts.length < 1 &&
            <> ({surveyContacts.length})</>
            }
            </span>
            {surveyContacts && !surveyContacts.length < 1 && !limitedEditing &&
            <span className={"empty-list"}>
                {!showEmptyConfirm ?
                <button className={"text"} onClick={()=>setShowEmptyConfirm(true)}>
                    {intl.formatMessage(
                        {
                            id: 'survey.contacts.emptyList',
                            defaultMessage: 'Empty List',
                        })}
                </button> :
                    <span>
                        {intl.formatMessage(
                            {
                                id: 'areYouSure',
                                defaultMessage: 'Are you sure?',
                            })} <button className={"text red"} onClick={()=>emptySurveyOfContacts()}>
                    {intl.formatMessage(
                        {
                            id: 'yes',
                            defaultMessage: 'Yes',
                        })}
                </button> / <button className={"text"} onClick={()=>setShowEmptyConfirm(false)}>
                    {intl.formatMessage(
                        {
                            id: 'no',
                            defaultMessage: 'No',
                        })}
                </button>
                    </span>
                }

            </span>
            }
        </h4>
        <div>
            {!surveyContacts || surveyContacts.length < 1 ?
                <p className={"placeholder"}>
                    {intl.formatMessage(
                        {
                            id: 'survey.contacts.noRecipients',
                            defaultMessage: 'This survey doesn\'t have any recipients yet. Start by adding from the table below.',
                        })}
                </p> :
                <>
                    <div className={"frame"}>
                        <div className={"recipients-container"}>
                            {studentList.map((obj, i) => {
                                let student = getStudentFromContacts(obj, contactsData)
                                let name = getStudentName(student, intl)
                                return <div className="block round" key={i}>
                                    <span>{name}</span>
                                    {!limitedEditing &&
                                    <div className={"close-icon"} onClick={() => removeStudentFromSurvey(obj)}>
                                        <CloseIcon/>
                                    </div>
                                    }
                                </div>
                            })}
                            {surveyContacts.length > 8 &&
                            <button className={"block round show-all"} onClick={()=>setShowContacts(!showContacts)}>
                                {!showContacts ?
                                    <>
                                        <Maximize />
                                        {intl.formatMessage(
                                            {
                                                id: 'showAll',
                                                defaultMessage: 'Show All',
                                            })
                                        }</> :
                                    <>
                                        <Minimize />
                                        {intl.formatMessage(
                                            {
                                                id: 'minimize',
                                                defaultMessage: 'Minimize',
                                            })}
                                    </>
                                }
                            </button>
                            }
                        </div>
                    </div>
                    <h4>
                        <span className={!showSummary ? "hidden" : ""}>
                        {intl.formatMessage(
                            {
                                id: 'survey.contacts.summary',
                                defaultMessage: 'Summary',
                            })}
                            </span>
                        <button className={"text"} onClick={()=> {
                            localStorage.setItem("showRecipientSummary", JSON.stringify(!showSummary))
                            setShowSummary(!showSummary)
                        }}>
                            {showSummary ?
                                intl.formatMessage(
                                    {
                                        id: 'hide',
                                        defaultMessage: 'Hide',
                                    }) :
                                intl.formatMessage(
                                    {
                                        id: 'show',
                                        defaultMessage: 'Show',
                                    })}
                        </button>
                    </h4>
                    {showSummary &&
                    <RenderSummary />
                    }
                </>
            }
        </div>
    </div>
}

function TableRender({dataFromUpload, contactsData, contactsColumns, supportRolesData, refetchContacts, refetchingContacts}) {
    const intl = useIntl()
    const contactInfoData = contactsData.map(contact => {
        if (contact.hasOwnProperty("meta")) {
            contact["meta"]["id"] = contact?.id
            contact["meta"]["annieuser"] = contact?.annieuser
            contact["meta"]["optout"] = contact?.optout
            contact["meta"]["updated"] = contact?.updated
            contact["meta"]["updatedby"] = contact?.updatedby
            // Add role info to meta
            for (let i = 0; i < supportRolesData.length; i++) {
                const role = supportRolesData[i]
                const roleObj = contact?.roles
                if (typeof roleObj === "object" && roleObj !== null) {
                    contact["meta"][role] = contact?.roles[role]
                }
            }
            // Change boolean values to strings for table
            const metaKeys = Object.keys(contact.meta)
            for (let i = 0; i < metaKeys.length; i++) {
                const metaKey = metaKeys[i]
                if (typeof contact["meta"][metaKey] === "boolean") {
                    contact["meta"][metaKey] = contact["meta"][metaKey].toString()
                }
            }
        }
        return contact.hasOwnProperty("meta") && contact["meta"]
    })

    let contactHeaders = contactsColumns || []
    for (let i = 0; i < supportRolesData.length; i++) {
        const supportRole = supportRolesData[i]
        if (!contactHeaders.includes(supportRole)) {
            contactHeaders.push(supportRole)
        }
    }

    const headerName = (column) => {
        if (supportRolesData.includes(column) || column === "annieuser") {
            return intl.formatMessage({
                id: "role."+column,
                defaultMessage: column
            })
        }
        return intl.formatMessage({
            id: column,
            defaultMessage: column
        })
    }

    const isLinkedToAnnieUser = header => {
        return header === "annieuser" || supportRolesData.includes(header)
    }

    const columnArray = contactHeaders.map( header => {
        const useDropdown = header === 'group' || header === 'location' || header === 'degree'
        const date = header === "updated"
        const showUserIcon = isLinkedToAnnieUser(header)
        if (useDropdown) {
            return {
                Header: headerName(header),
                accessor: header,
                Filter: MultiSelectColumnFilter,
                filter: filterIncludes,
                width: 200
            }
        }
        if (showUserIcon) {
            return {
                Header: <div title={headerName(header)} className={"flex-header"}><UserIcon/><span>{headerName(header)}</span></div>,
                accessor: header,
                width: 200
            }
        }
        if (date) {
            return {
                Header: headerName(header),
                accessor: header,
                width: 200,
                Cell: ({cell: {value}}) => (
                    <>{new Date(value).toLocaleString([intl.locale], {year:'numeric',month:'numeric',day:'numeric',hour: '2-digit', minute:'2-digit'})}</>
                )
            }
        }
        return {
            Header: headerName(header),
            accessor: header,
            width: 200
        }
    })

    const data = useMemo(() => contactInfoData, [contactInfoData])
    const columns = useMemo(()=> columnArray, [columnArray])

    function MultiSelectColumnFilter({column: { filterValue, setFilter, preFilteredRows, id }}) {
        const [showOptions, setShowOptions] = useState(false)
        const refOptions = useRef(null)
        const refOptionsToggle = useRef(null)
        useEffect(()=>{
            document.addEventListener("mousedown", handleClickOutside)
            function handleClickOutside(event) {
                if (refOptions.current && !refOptions.current.contains(event.target) && !refOptionsToggle.current.contains(event.target)) {
                    setShowOptions(false)
                }
                return () => {
                    document.removeEventListener("mousedown", handleClickOutside);
                }
            }
        },[refOptions])

        const options = React.useMemo(() => {
            const options = new Set()
            preFilteredRows.forEach(row => {
                options.add(row.values[id])
            })
            options.delete(null)
            options.add("missing")
            return [...options.values()]
        }, [id, preFilteredRows])

        const toggleFilter = option => {
            if (!filterValue) {
                setFilter([option])
            } else if (filterValue.includes(option)) {
                const index = filterValue.indexOf(option)
                if (index > -1) {
                    const newFilter = filterValue
                    newFilter.splice(index, 1)
                    setFilter(newFilter)
                }
            } else {
                const newFilter = filterValue
                newFilter.push(option)
                setFilter(newFilter)
            }
        }

        return <>
            <div className={"selected-filters-container"} onClick={e=>e.stopPropagation()}>
                <div ref={refOptionsToggle} className={"selected-filters"} onClick={()=>setShowOptions(!showOptions)}>
                    {filterValue ?
                        filterValue.length < 1 ? intl.formatMessage(
                            {
                                id: 'survey.table.showAll',
                                defaultMessage: 'Show All',
                            }) :
                        filterValue.map((filter, i) => {
                            if (filterValue.length === 1) {
                                return filter
                            }
                            if (filterValue.length === i + 1) {
                                return filter
                            }
                            return filter + ", "
                        }) : intl.formatMessage(
                            {
                                id: 'survey.table.showAll',
                                defaultMessage: 'Show All',
                            })
                    }
                </div>
                {showOptions &&
                <div ref={refOptions} className={"filter-options"}>
                    <div onClick={()=>setFilter("")}>
                        <input type={"checkbox"} readOnly={true} checked={filterValue === "" || filterValue === [] || !filterValue || filterValue.length < 1} />
                        {intl.formatMessage(
                            {
                                id: 'survey.table.showAll',
                                defaultMessage: 'Show All',
                            })}
                    </div>
                    {options.map((option, i) => (
                        <div key={i} onClick={()=>toggleFilter(option)}>
                            <input type={"checkbox"} readOnly={true} checked={filterValue !== undefined && filterValue && filterValue.includes(option)} />
                            {option === "missing" ?
                                intl.formatMessage(
                                    {
                                        id: 'survey.table.empty',
                                        defaultMessage: '(Empty)',
                                    })
                                : option}
                        </div>
                    ))}
                </div>
                }
            </div>
        </>
    }

    function filterIncludes(rows, id, filterValue) {
        return rows.filter(row => {
            const rowValue = row.values[id]
            if (!filterValue || filterValue.length < 1) {
                return true
            }
            if (filterValue.includes("missing") && !rowValue) {
                return true
            }
            return filterValue.includes(rowValue)
        })
    }

    return <Table columns={columns} data={data} contactHeaders={contactHeaders} dataFromUpload={dataFromUpload} supportRolesData={supportRolesData} refetchContacts={refetchContacts} refetchingContacts={refetchingContacts}/>
}

export function GlobalFilter({globalFilter, setGlobalFilter, students, usersSearch}) {
    const intl = useIntl()
    const [value, setValue] = useState(globalFilter)
    const onChange = useAsyncDebounce(value => {
        setGlobalFilter(value || undefined)
    }, 50)

    return <input value={value || ""} className={"search-user"} type={"text"}
                  onChange={e => {
                      setValue(e.target.value);
                      onChange(e.target.value);
                  }}
                  placeholder={usersSearch ? intl.formatMessage(
                      {
                          id: 'users.searchFromUsers',
                          defaultMessage: 'Search from {n} users',
                      }, {
                          n: students
                      }
                  ) :
                      intl.formatMessage(
                          {
                              id: 'survey.contacts.searchFromContacts',
                              defaultMessage: 'Search from {n} students',
                          }, {
                              n: students
                          }
                      )
                  }
    />
}

function Table({columns, data, contactHeaders, dataFromUpload, supportRolesData, refetchContacts, refetchingContacts}) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const limitedEditing = surveyData.status === "IN PROGRESS" || surveyData.status === "FINISHED"
    const intl = useIntl()

    function DefaultColumnFilter({column: { filterValue, setFilter }}) {
        return (
            <input
                type={"search"}
                onClick={e => e.stopPropagation()}
                value={filterValue || ''}
                onChange={e => {setFilter(e.target.value || undefined)}}
                placeholder={intl.formatMessage({id:"search", defaultMessage:"Search"})}
            />
        )
    }

    const IndeterminateCheckbox = React.forwardRef(
        ({ indeterminate, ...rest }, ref) => {
            const defaultRef = React.useRef()
            const resolvedRef = ref || defaultRef

            React.useEffect(() => {
                resolvedRef.current.indeterminate = indeterminate
            }, [resolvedRef, indeterminate])

            return <input type="checkbox" ref={resolvedRef} {...rest} />
        }
    )

    const defaultColumn = React.useMemo(() => ({
            Filter: DefaultColumnFilter
    }), [])

    const pageSizeFromStorage = localStorage.getItem("pageSize") ? Number(localStorage.getItem("pageSize")) : 50
    const hiddenColumnsFromStorage = localStorage.getItem("hiddenColumns") ? JSON.parse(localStorage.getItem("hiddenColumns")) : []
    const columnOrderFromStorage = localStorage.getItem("columnOrder") ? JSON.parse(localStorage.getItem("columnOrder")) : []
    const filtersFromStorage = sessionStorage.getItem("contactTableFilters") ? JSON.parse(sessionStorage.getItem("contactTableFilters")) : []

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        prepareRow,
        selectedFlatRows,
        preGlobalFilteredRows,
        setGlobalFilter,
        state,
        state: {pageIndex, pageSize, filters},
        setHiddenColumns,
        setColumnOrder,
        setAllFilters
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            initialState: {
                pageIndex: 0,
                pageSize: pageSizeFromStorage,
                hiddenColumns: hiddenColumnsFromStorage,
                columnOrder: columnOrderFromStorage,
                filters: filtersFromStorage
            }
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        useFlexLayout,
        usePagination,
        useRowSelect,
        useColumnOrder,
        hooks => {
            if (!limitedEditing) {
                hooks.visibleColumns.push(columns => [
                    {
                        id: 'selection',
                        width: 36,
                        Header: ({getToggleAllRowsSelectedProps}) => (
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        ),
                        Cell: ({row}) => (
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        ),
                    },
                    ...columns,
                ])
            }
        }
    )

    const hideColumns = hiddenColumns => {
        setHiddenColumns(hiddenColumns)
        localStorage.setItem("hiddenColumns", JSON.stringify(hiddenColumns))
    }

    const orderColumns = columnOrder => {
        setColumnOrder(columnOrder)
        localStorage.setItem("columnOrder", JSON.stringify(columnOrder))
    }

    useEffect(()=>{
        sessionStorage.setItem("contactTableFilters", JSON.stringify(filters))
    },[filters])

    const selectedContactsId = selectedFlatRows.map(row => row.original.id)
    const AddContactsToSurvey = () => {
        let newSurveyData = {...surveyData}
        for (let i = 0; i < selectedContactsId.length; i++) {
            if (!newSurveyData.contacts) {
                newSurveyData.contacts = []
            }
            if (!newSurveyData.contacts.includes(selectedContactsId[i])) {
                newSurveyData.contacts.push(selectedContactsId[i])
            }
        }
        setSurveyData(newSurveyData)
    }

    const RemoveContactsFromSurvey = () => {
        let newSurveyData = {...surveyData}
        for (let i = 0; i < selectedContactsId.length; i++) {
            if (newSurveyData.contacts.includes(selectedContactsId[i])) {
                const index = newSurveyData.contacts.indexOf(selectedContactsId[i])
                if (index > -1) {
                    newSurveyData.contacts.splice(index, 1)
                }
            }
        }
        setSurveyData(newSurveyData)
    }

    useEffect(()=>{
        if (dataFromUpload && dataFromUpload.length > 0) {
            AddContactsToSurveyFromUpload()
        }
    },[dataFromUpload])

    const AddContactsToSurveyFromUpload = () => {
        let newSurveyData = {...surveyData}
        for (let i = 0; i < dataFromUpload.length; i++) {
            if (!newSurveyData.contacts) {
                newSurveyData.contacts = []
            }
            if (!newSurveyData.contacts.includes(dataFromUpload[i])) {
                newSurveyData.contacts.push(dataFromUpload[i])
            }
        }
        setSurveyData(newSurveyData)
    }

    return <div className={"contact-table-container"}>
        <h4>
            {intl.formatMessage(
                {
                    id: 'survey.contacts.allContacts',
                    defaultMessage: 'All Contacts',
                })}
        </h4>
        <div className={"toolbar-container"}>
            <div className={"filters"}>
                <GlobalFilter
                    preGlobalFilteredRows={preGlobalFilteredRows}
                    globalFilter={state.globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    students={rows.length}
                />
                <FiltersDisplay filters={filters} removeFilters={()=>setAllFilters([])}/>
                <button className={refetchingContacts ? "icon-button refresh loading" : "icon-button refresh"} onClick={refetchContacts} title={intl.formatMessage({id:"refetchContacts", defaultMessage:"Update contacts"})}>
                    <RefreshIcon />
                </button>
            </div>
            <div className={"buttons"}>
                <ColumnEditor contactHeaders={contactHeaders}
                              hideColumns={hiddenColumns=>hideColumns(hiddenColumns)}
                              orderColumns={columnOrder=>orderColumns(columnOrder)}
                              supportRolesData={supportRolesData}
                />
                {!limitedEditing &&
                <>
                    <button disabled={selectedFlatRows.map(row => row.original.id).length < 1} className={"remove"} onClick={()=>RemoveContactsFromSurvey()}>
                        <DownArrow/>
                        {intl.formatMessage(
                        {
                            id: 'survey.contacts.removeFromSurvey',
                            defaultMessage: 'Remove Selected',
                        })}
                        {selectedFlatRows && selectedFlatRows.length > 0 &&
                        <> ({selectedFlatRows.length})</>}
                    </button>
                    <button disabled={selectedFlatRows.map(row => row.original.id).length < 1} className={"add"} onClick={()=>AddContactsToSurvey()}>
                        <UpArrow/>
                        {intl.formatMessage(
                        {
                            id: 'survey.contacts.addToSurvey',
                            defaultMessage: 'Add Selected',
                        })}
                        {selectedFlatRows && selectedFlatRows.length > 0 &&
                        <> ({selectedFlatRows.length})</>}
                    </button>
                </>
                }
            </div>
        </div>
        <div className={"table-container"}>
            <table {...getTableProps()} className={"contact-table"}>
                <thead>
                {
                    headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <th {...column.getHeaderProps(column.getSortByToggleProps({title:undefined}))}>
                                    <div className={"header"} title={column.render('Header')}>
                                        <span className={"text"}>{column.render('Header')}</span>
                                        {column.isSorted &&
                                        <>
                                            {column.isSortedDesc ?
                                                ' 🔽' :
                                                ' 🔼'
                                            }
                                        </>
                                        }
                                    </div>
                                    {column.canFilter ? <div className={column.filterValue ? "filter-active": ""}>{column.render('Filter')}</div> : null}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {
                    page.map(row => {

                        prepareRow(row)
                        return (
                            <tr className={surveyData.contacts && surveyData.contacts.includes(row.original.id) ? "in-survey" : ""} {...row.getRowProps()}>
                                {row.cells.map(cell => {
                                    return <td title={cell.value} {...cell.getCellProps()}>
                                        {cell.render('Cell')}
                                    </td>
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
        <div className="pagination">
            <select
                value={pageSize}
                onChange={e => {
                    setPageSize(Number(e.target.value))
                    localStorage.setItem("pageSize", e.target.value)
                }}
            >
                {[10, 50, 100, 200, 500].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                        {intl.formatMessage(
                            {
                                id: 'surveys.recipients.table.show',
                                defaultMessage: 'Show',
                            })}{' '}
                        {pageSize}
                    </option>
                ))}
            </select>
            <div className={"page-index"}>
                {intl.formatMessage(
                    {
                        id: 'surveys.recipients.table.page',
                        defaultMessage: 'Page',
                    })}{' '}
                <strong>
            {pageIndex + 1}{' '}
                    {intl.formatMessage(
                        {
                            id: 'surveys.recipients.table.of',
                            defaultMessage: 'of',
                        })}
                    {' '}{pageOptions.length}
          </strong>
        </div>
            <div className={"buttons"}>
                <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                    <First/>
                </button>{' '}
                <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                    <Previous/>
                </button>{' '}
                <button onClick={() => nextPage()} disabled={!canNextPage}>
                    <Next />
                </button>{' '}
                <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                    <Last/>
                </button>
            </div>
        </div>
    </div>
}